
import React, { HTMLAttributes, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Col, InfoBox, Row, useUI } from '@evertel/web/ui'
import { InviteByEmailForm } from './InviteByEmailForm'
import { useService } from '@evertel/di'
import { DepartmentSeatStatusController, SendInvitesBody, SendInvitesController } from '@evertel/invites'

interface DepartmentInviteFormProps extends HTMLAttributes<HTMLDivElement|HTMLSpanElement> {
    inviteBody?: SendInvitesBody
    invitedRooms?: { label: string, value: number }[]
    departmentId: number
    onClose: () => void
}

const DepartmentInviteForm: React.FC<DepartmentInviteFormProps> = observer(({
    departmentId,
    onClose
}) => {

    const sendInvitesController = useService(SendInvitesController, [])
    const seatStatusController = useService(DepartmentSeatStatusController, [departmentId])

    const { addToast } = useUI()

    const [isLoading, setIsLoading] = useState(false)
    const [inviteBody, setInviteBody] = useState<SendInvitesBody>()
    const [error, setError] = useState<string>()

    useEffect(() => {
        seatStatusController.init(departmentId)
    }, [seatStatusController, departmentId])

    // GCWSCSR-915: seat status for member invites (all department invite roles consume a seat)
    const capacities = seatStatusController.capacities
    const pendingInviteCount = seatStatusController.pendingSeatInviteCount
    const newInviteCount = inviteBody?.to?.length || 0
    const seatsFull = seatStatusController.isFull
    const showSeatWarning = !seatsFull && newInviteCount > 0 && seatStatusController.willExceedSeats(newInviteCount)

    const send = async () => {
        if (seatsFull) return

        if (!inviteBody?.to?.length || !inviteBody?.roles?.length) {
            setError('Please make sure you have at least one invite to send and have set their role')
            return
        }

        setIsLoading(true)

        try {
            await sendInvitesController.sendDepartmentInvite(departmentId, inviteBody as SendInvitesBody)

            addToast({
                color: 'success',
                message: 'Your invites have been sent!'
            })

            onClose()

        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }

        setIsLoading(false)
    }

    return (
        <Col>
            {(seatsFull) ?
                <InfoBox color="danger">
                    Your agency is using all {capacities?.verifiedMemberCapacity} of its member seats.
                    New member invites can't be sent until seats become available.
                    Contact your administrator or Customer Success Manager to add more seats.
                </InfoBox>
                : (showSeatWarning) ?
                    <InfoBox color="warning">
                        Heads up: your agency has {capacities?.verifiedMembersAvailable} available
                        seat{capacities?.verifiedMembersAvailable === 1 ? '' : 's'}
                        {(pendingInviteCount > 0) && ` and ${pendingInviteCount} pending member invite${pendingInviteCount === 1 ? '' : 's'} already out`}.
                        Sending {newInviteCount} more invite{newInviteCount === 1 ? '' : 's'} may exceed your
                        seat limit — invites beyond the limit can't be accepted until more seats are added.
                    </InfoBox>
                    :
                    <InfoBox color="info">
                        Agency members can access rooms, EverDocs, and your full agency directory. This is best for agency employees.
                    </InfoBox>
            }
            {(error) &&
                <InfoBox
                    color="danger"
                    className="fadeIn">
                    {error}
                </InfoBox>
            }
            <div style={seatsFull ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
                <InviteByEmailForm
                    departmentId={departmentId}
                    className="mt-4"
                    inviteType="department"
                    showRole
                    onChange={setInviteBody}
                />
            </div>
            <Row
                align="center"
                className="mt-4">
                <Button
                    color="primary"
                    loading={isLoading}
                    disabled={isLoading || seatsFull}
                    onClick={send}>
                    Send Invites
                </Button>
            </Row>
        </Col>
    )
})

export { DepartmentInviteForm }
